import { add, dot, scale } from "./vector.js";
import type { Brush, MapData, Plane, Vec3, Trigger } from "./types.js";
const EPS = 0.0001;
export type Trace = { fraction: number; normal: Vec3; solid: boolean };
export function planes(b: Brush): Plane[] {
  return (
    b.planes ?? [
      { n: [1, 0, 0], d: b.max[0] },
      { n: [-1, 0, 0], d: -b.min[0] },
      { n: [0, 1, 0], d: b.max[1] },
      { n: [0, -1, 0], d: -b.min[1] },
      { n: [0, 0, 1], d: b.max[2] },
      { n: [0, 0, -1], d: -b.min[2] },
    ]
  );
}
export function traceBrush(
  p: Vec3,
  delta: Vec3,
  b: Brush,
  height: number,
): Trace {
  let enter = -Infinity,
    leave = 1,
    normal: Vec3 = [0, 0, 0],
    outside = false;
  for (const plane of planes(b)) {
    const n = plane.n;
    const expanded =
      plane.d +
      16 * (Math.abs(n[0]) + Math.abs(n[1])) +
      (n[2] < 0 ? -n[2] * height : 0);
    const start = dot(p, n) - expanded,
      end = start + dot(delta, n);
    if (start >= -EPS) outside = true;
    if (start > 0 && end >= start)
      return { fraction: 1, normal: [0, 0, 0], solid: false };
    if (start <= 0 && end <= 0) continue;
    if (start > end) {
      const t = (start - EPS) / (start - end);
      if (t > enter) {
        enter = t;
        normal = n;
      }
    } else leave = Math.min(leave, (start + EPS) / (start - end));
  }
  if (!outside) return { fraction: 0, normal: [0, 0, 1], solid: true };
  return enter < leave && enter > -1 && enter < 1
    ? { fraction: Math.max(0, enter), normal, solid: false }
    : { fraction: 1, normal: [0, 0, 0], solid: false };
}
export class World {
  private grid = new Map<string, Brush[]>();
  private large: Brush[] = [];
  constructor(public map: MapData) {
    for (const b of map.brushes) {
      if (
        (Math.ceil((b.max[0] - b.min[0]) / 256) + 1) *
          (Math.ceil((b.max[1] - b.min[1]) / 256) + 1) >
        16384
      ) {
        this.large.push(b);
        continue;
      }
      for (
        let x = Math.floor(b.min[0] / 256);
        x <= Math.floor(b.max[0] / 256);
        x++
      )
        for (
          let y = Math.floor(b.min[1] / 256);
          y <= Math.floor(b.max[1] / 256);
          y++
        ) {
          const key = x + "," + y;
          const cell = this.grid.get(key) ?? [];
          cell.push(b);
          this.grid.set(key, cell);
        }
    }
  }
  trace(
    p: Vec3,
    delta: Vec3,
    height: number,
    tick = 0,
    ignorePlatforms = false,
  ): Trace {
    let best: Trace = { fraction: 1, normal: [0, 0, 0], solid: false };
    const candidates = new Set<Brush>(this.large);
    for (
      let x = Math.floor((Math.min(p[0], p[0] + delta[0]) - 16) / 256);
      x <= Math.floor((Math.max(p[0], p[0] + delta[0]) + 16) / 256);
      x++
    )
      for (
        let y = Math.floor((Math.min(p[1], p[1] + delta[1]) - 16) / 256);
        y <= Math.floor((Math.max(p[1], p[1] + delta[1]) + 16) / 256);
        y++
      )
        for (const b of this.grid.get(x + "," + y) ?? []) candidates.add(b);
    for (const platform of ignorePlatforms ? [] : (this.map.platforms ?? [])) {
      const offset = scale(
        platform.axis,
        Math.sin((tick / 100 / platform.period) * Math.PI * 2) *
          platform.amplitude,
      );
      candidates.add({
        ...platform.brush,
        min: add(platform.brush.min, offset),
        max: add(platform.brush.max, offset),
        planes: platform.brush.planes?.map((p) => ({
          n: p.n,
          d: p.d + dot(p.n, offset),
        })),
      });
    }
    for (const b of candidates) {
      const t = traceBrush(p, delta, b, height);
      if (t.solid || t.fraction < best.fraction) best = t;
    }
    return best;
  }
}
export const inBox = (p: Vec3, min: Vec3, max: Vec3) =>
  p.every((v, i) => v >= min[i] && v <= max[i]);

// Imported Source trigger volumes test the player hull; authored maps retain feet-point zones.
export function touchesTrigger(p: Vec3, t: Trigger, height: number) {
  if (t.volumes)
    return t.volumes.some((b) =>
      planes(b).every((plane) => {
        const n = plane.n,
          expansion = t.hullTouch
            ? 16 * (Math.abs(n[0]) + Math.abs(n[1])) +
              (n[2] < 0 ? -n[2] * height : 0)
            : 0;
        return dot(p, n) <= plane.d + expansion + EPS;
      }),
    );
  return t.hullTouch
    ? inBox(
        p,
        [t.min[0] - 16, t.min[1] - 16, t.min[2] - height],
        [t.max[0] + 16, t.max[1] + 16, t.max[2]],
      )
    : inBox(p, t.min, t.max);
}
